/**
 * VH-206: the review filter's list — past entries whose machine nobody stated.
 *
 * A FILTER on the session history, not a screen of its own (decided
 * 2026-09-10): the backlog shrinks towards zero once the backfill lands, and a
 * dedicated screen for a vanishing list becomes an empty room.
 *
 * Assigning writes through the existing PUT session-exercises/{id}; the list is
 * then refetched so the entry leaves it from the server's answer, not a guess.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { weeklyProgramsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import MachineChoice from './MachineChoice'
import {
  UNATTRIBUTED_QUERY_KEY,
  useUnattributedExercises,
} from '../../hooks/useUnattributedExercises'


export default function UnattributedMachineReview() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading, isError, refetch } = useUnattributedExercises()
  const [errorFor, setErrorFor] = useState(null)

  const assign = useMutation({
    mutationFn: ({ sessionExerciseId, equipmentId }) =>
      weeklyProgramsAPI.updateExercise(sessionExerciseId, {
        performed_equipment_id: equipmentId,
      }),
    onSuccess: () => {
      setErrorFor(null)
      queryClient.invalidateQueries({ queryKey: UNATTRIBUTED_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['weekly-program'] })
    },
    onError: (error, vars) =>
      setErrorFor({
        id: vars.sessionExerciseId,
        message: apiErrorMessage(error, t('common.error', 'An error occurred')),
      }),
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3" data-testid="unattributed-loading">
        <div className="h-24 bg-gray-200 rounded" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="card p-6 text-center" data-testid="unattributed-error">
        <p className="text-gray-700 mb-4">{t('machineReview.loadFailed')}</p>
        <button onClick={() => refetch()} className="btn-primary">
          {t('common.retry')}
        </button>
      </div>
    )
  }

  const entries = data?.entries ?? []
  if (entries.length === 0) {
    return (
      <div className="card p-6 text-center text-gray-700" data-testid="unattributed-empty">
        {t('machineReview.empty')}
      </div>
    )
  }

  const locale = i18n?.language || 'en'

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3" data-testid="unattributed-total">
        {t('machineReview.intro', { count: data.total })}
      </p>
      <ul className="space-y-3">
        {entries.map((entry) => {
          const id = entry.session_exercise_id
          const pending = assign.isPending && assign.variables?.sessionExerciseId === id
          return (
            <li key={id} className="card pb-4" data-testid={`unattributed-${id}`}>
              <div className="px-4 pt-4">
                <div className="font-semibold text-gray-900 break-words">{entry.exercise_name}</div>
                <div className="text-sm text-gray-600 mt-0.5">
                  {entry.session_date
                    ? new Date(`${entry.session_date}T00:00:00`).toLocaleDateString(locale)
                    : t('machineReview.noDate')}
                  {entry.location_name ? ` · ${entry.location_name}` : ''}
                </div>
              </div>
              <MachineChoice
                candidates={entry.candidates || []}
                value={null}
                disabled={pending}
                onChange={(equipmentId) =>
                  equipmentId != null &&
                  assign.mutate({ sessionExerciseId: id, equipmentId })
                }
                testIdPrefix={`unattributed-machine-${id}`}
              />
              {errorFor?.id === id && (
                <p className="px-4 pt-2 text-sm text-red-600" data-testid={`unattributed-error-${id}`}>
                  {errorFor.message}
                </p>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
